import { parseColour, resolveColour, resolvePresent, buttonLabelColour, LEGACY_ACCENT } from "./inAppColour";
// In-App dark appearance (DARK-MODE.md: D-1 / R-2 / D-5). Portal mirror of the SDK contract.
// Authoring keys: every colour key K may have a sibling `K + "Dark"`. `style.appearance` is
// "auto" | "light" | "dark"; absent → "auto" when any …Dark key exists anywhere, else "light".

export type Appearance = "auto" | "light" | "dark";
const APPEARANCES: Appearance[] = ["auto", "light", "dark"];

const hasDarkKey = (o: any): boolean =>
  !!o && typeof o === "object" && Object.keys(o).some((k) => k.length > 4 && k.endsWith("Dark") && typeof o[k] === "string" && o[k].trim() !== "");

function blocksHaveDark(bs: any[]): boolean {
  return (bs || []).some((b) => {
    const c = b?.content || {};
    return hasDarkKey(c)
      || (Array.isArray(c.slides) && c.slides.some(hasDarkKey))
      || (Array.isArray(c.children) && blocksHaveDark(c.children));
  });
}

export function hasDarkValues(pages: { blocks: any[]; style?: any }[]): boolean {
  return (pages || []).some((p) => hasDarkKey(p?.style) || blocksHaveDark(p?.blocks));
}

export function effectiveAppearance(chosen: unknown, pages: { blocks: any[]; style?: any }[]): Appearance {
  if (APPEARANCES.includes(chosen as Appearance)) return chosen as Appearance;
  return hasDarkValues(pages) ? "auto" : "light";
}

// ─── Device resolution (shared vectors: fixtures/inapp-dark-vectors.json, push-ios) ────────────

const isDarkKey = (k: string, v: unknown) => k.length > 4 && k.endsWith("Dark") && typeof v === "string" && v.trim() !== "";
export function payloadHasDarkKey(x: unknown): boolean {
  if (Array.isArray(x)) return x.some(payloadHasDarkKey);
  if (!x || typeof x !== "object") return false;
  return Object.entries(x as Record<string, unknown>).some(([k, v]) => isDarkKey(k, v) || payloadHasDarkKey(v));
}

// Effective appearance for a device payload on a device in `device` mode.
export function payloadAppearance(payload: any, device: "light" | "dark"): "light" | "dark" {
  const a = String(payload?.presentation?.appearance ?? "").toLowerCase();
  if (a === "light" || a === "dark") return a;
  if (a === "auto") return device;
  return payloadHasDarkKey(payload) ? device : "light";
}

// D-5 dark defaults by role.
export const DARK_DEFAULTS: Record<string, string> = {
  surface: "#1C1C1E", text: "#F2F2F7", secondaryText: "#8E8E93", placeholder: "#8E8E93",
  inputFill: "#2C2C2E", inputBorder: "#3A3A3C", divider: "#3A3A3C", containerBorder: "#3A3A3C",
  videoFrame: "#000000", glyph: "#8E8E93", closeGlyph: "#F2F2F7", closeDisc: "#2C2C2E",
};
export const darkRoleDefault = (role: string, brandColor?: unknown, brandColorDark?: unknown): string =>
  role === "accent"
    ? (parseColour(brandColorDark) || parseColour(brandColor) || (parseColour(LEGACY_ACCENT) as string))
    : (parseColour(DARK_DEFAULTS[role] || DARK_DEFAULTS.text) as string);

export interface ColourQuery {
  appearance: "light" | "dark"; role: string; key: string; object: Record<string, any>;
  lightDefault?: string; brandColor?: unknown; brandColorDark?: unknown;
}
export function darkModeColour(q: ColourQuery): string {
  const o = q.object || {};
  if (q.appearance !== "dark") return resolveColour(o[q.key], q.lightDefault || "#000000", q.brandColor);
  const d = parseColour(o[q.key + "Dark"]);
  if (d) return d;
  const base = o[q.key];
  if (typeof base === "string" && base.trim()) {
    const brand = parseColour(q.brandColorDark) ? q.brandColorDark : q.brandColor;
    const r = resolvePresent(base, brand);
    if (r) return r;
  }
  return darkRoleDefault(q.role, q.brandColor, q.brandColorDark);
}

export function darkModeButtonLabel(i: { appearance: string; style: string; color: string; textColor?: unknown; textColorDark?: unknown; background: string; brandColor?: unknown }): string {
  if (i.appearance !== "dark") return buttonLabelColour(i.style, i.color, i.textColor, i.background, i.brandColor);
  const text = parseColour(i.textColorDark) ? i.textColorDark : i.textColor;
  return buttonLabelColour(i.style, i.color, text, i.background, i.brandColor, "#F2F2F7");
}

// ─── Portal Preview (DA-BUG-1054) ──────────────────────────────────────────────────────────────
// The authoring content keys and the role each takes in dark appearance when nothing is authored.
// Dark siblings are `<portalKey>Dark`, so the same resolution runs on the portal's own keys.
const ROLE_OF: Record<string, Record<string, string>> = {
  heading: { color: "text" }, text: { color: "text" }, "feature-list": { color: "text" },
  button: { buttonColor: "accent" }, product: { buttonColor: "accent" },
  divider: { dividerColor: "divider" }, icon: { iconBg: "inputFill" },
  // DA-BUG-1421 (PB-1/D10): resolve the per-block colour keys in dark mode so the dark contrast check
  // compares the DARK colours (not the light ones) and the Preview renders them (digits/label/expiry/submit/progress label).
  countdown: { color: "text", labelColor: "text" },
  coupon: { accent: "accent", labelColor: "text", expiryColor: "text" },
  progress: { accent: "accent", trackColor: "inputFill", labelColor: "text" },
  rating: { activeColor: "accent", submitColor: "accent" },
  container: { containerBg: "surface", containerBorderColor: "containerBorder" },
  form: { containerBg: "surface", containerBorderColor: "containerBorder" },
};
// "#RRGGBBAA" → a CSS colour the browser renders.
export const cssOf = (c: string): string => {
  const a = parseInt(c.slice(7, 9), 16);
  return a === 255 ? c.slice(0, 7) : `rgba(${parseInt(c.slice(1, 3), 16)},${parseInt(c.slice(3, 5), 16)},${parseInt(c.slice(5, 7), 16)},${Math.round((a / 255) * 100) / 100})`;
};

export interface Brand { brandColor?: string | null; brandColorDark?: string | null }

// Blocks with every colour key replaced by its dark-appearance value (as the SDK resolves it).
// `surface` is the resolved background the blocks sit on (for the button-label rule).
export function darkPreviewBlocks<T extends { type: string; content: any }>(blocks: T[], surface: string, brand: Brand = {}): T[] {
  return (blocks || []).map((b) => {
    const c = { ...(b.content || {}) };
    const roles = ROLE_OF[b.type] || {};
    Object.entries(roles).forEach(([key, role]) => {
      c[key] = cssOf(darkModeColour({ appearance: "dark", role, key, object: b.content || {}, brandColor: brand.brandColor, brandColorDark: brand.brandColorDark }));
    });
    if (b.type === "button" || b.type === "product") {
      const fill = parseColour(c.buttonColor) as string;
      c.buttonTextColor = cssOf(darkModeButtonLabel({
        appearance: "dark", style: b.type === "product" ? "primary" : (b.content?.buttonStyle || "primary"),
        color: fill, textColor: b.content?.buttonTextColor, textColorDark: b.content?.buttonTextColorDark, background: surface, brandColor: brand.brandColor,
      }));
    }
    if (b.type === "rating") { // DA-BUG-1421 (D9): Submit label stays readable on the dark submit fill
      const fill = parseColour(c.submitColor) as string;
      c.submitTextColor = cssOf(darkModeButtonLabel({
        appearance: "dark", style: "primary", color: fill,
        textColor: b.content?.submitTextColor, textColorDark: b.content?.submitTextColorDark, background: surface, brandColor: brand.brandColor,
      }));
    }
    if (Array.isArray(c.children)) {
      const inner = b.type === "container" || b.type === "form" ? (parseColour(c.containerBg) || surface) : surface;
      c.children = darkPreviewBlocks(c.children, inner, brand);
    }
    return { ...b, content: c };
  });
}
export const darkPreviewSurface = (style: { background?: string; backgroundDark?: string }, brand: Brand = {}): string =>
  darkModeColour({ appearance: "dark", role: "surface", key: "background", object: style || {}, brandColor: brand.brandColor, brandColorDark: brand.brandColorDark });
